import { useMemo } from 'react';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import { BUSINESS_QUERY_PARAM } from '@/modules/common/constants/search-params.ts';
import { employeeDashboardPaths } from '@/modules/employee-dashboard/routes.tsx';
import { SidebarMenuItem } from 'ui';
import {
  AlertGrayIcon,
  AlertWhiteIcon, // FileGreyIcon,
  // FileWhiteIcon,
  HomeGrayIcon,
  HomeWhiteIcon,
  LockLightGrayIcon,
  LockWhiteIcon,
  MeetingGrayIcon,
  MeetingWhiteIcon,
  TaskIcon,
  TasksInactive,
} from 'ui/icons';
import { TaskListMode } from 'compliance-api';

export const useSidebarOptions = (): SidebarMenuItem[] => {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const search = createSearchParams({
    [BUSINESS_QUERY_PARAM]: params.get(BUSINESS_QUERY_PARAM) || '',
  }).toString();

  return useMemo(
    () => [
      {
        label: 'Dashboard',
        icon: HomeGrayIcon,
        activeIcon: HomeWhiteIcon,
        disabledIcon: HomeGrayIcon,
        pathname: employeeDashboardPaths.static.dashboard,
        tooltipHelpText: 'View a summary of key compliance metrics and recent activity.',
        onClick: () => navigate({ pathname: employeeDashboardPaths.static.dashboard, search }),
      },
      {
        label: 'Tasks',
        activeIcon: TaskIcon,
        icon: TasksInactive,
        pathname: employeeDashboardPaths.static.tasks,
        opener: true,
        tooltipHelpText:
          'Complete forms on a given time and recurrency to stay on top of compliance regulations or deadlines.',

        subItems: [
          {
            label: 'To Do',
            pathname: employeeDashboardPaths.dynamic.taskCategory(TaskListMode.Todo),
            tooltipHelpText: 'View current tasks requiring your attention.',
            onClick: () => {
              navigate({
                pathname: employeeDashboardPaths.dynamic.taskCategory(TaskListMode.Todo),
                search: params.toString(),
              });
            },
          },
          {
            label: 'Upcoming',
            pathname: employeeDashboardPaths.dynamic.taskCategory(TaskListMode.Upcoming),
            tooltipHelpText: 'See tasks scheduled for the near future.',
            onClick: () => {
              navigate({
                pathname: employeeDashboardPaths.dynamic.taskCategory(TaskListMode.Upcoming),
                search: params.toString(),
              });
            },
          },
          {
            label: 'Overdue',
            pathname: employeeDashboardPaths.dynamic.taskCategory(TaskListMode.Overdue),
            tooltipHelpText:
              'Review tasks that are past their due date. These tasks still can be completed.',
            onClick: () => {
              navigate({
                pathname: employeeDashboardPaths.dynamic.taskCategory(TaskListMode.Overdue),
                search: params.toString(),
              });
            },
          },
          {
            label: 'History',
            pathname: employeeDashboardPaths.dynamic.taskCategory(TaskListMode.History),
            tooltipHelpText: 'A log of completed tasks.',
            onClick: () => {
              navigate({
                pathname: employeeDashboardPaths.dynamic.taskCategory(TaskListMode.History),
                search: params.toString(),
              });
            },
          },
        ],
        // onClick: () => navigate({ pathname: employeeDashboardPaths.static.tasks, search }),
      },
      {
        label: 'Events',
        icon: AlertGrayIcon,
        activeIcon: AlertWhiteIcon,
        disabledIcon: AlertGrayIcon,
        pathname: employeeDashboardPaths.static.incidents,
        tooltipHelpText:
          'Report important events as they happen, like incidents, breaches or employee trainings.',
        onClick: () =>
          navigate({
            pathname: employeeDashboardPaths.static.incidents,
            search,
          }),
      },
      {
        label: 'Meetings',
        icon: MeetingGrayIcon,
        activeIcon: MeetingWhiteIcon,
        disabledIcon: MeetingGrayIcon,
        searchKeywords: ['schedule', 'video', 'conference', 'meeting', 'call'],
        tooltipHelpText:
          'You can use this feature to create Microsoft Teams meetings and Calendar records for online and offline meetings.',
        onClick: () =>
          navigate({
            pathname: employeeDashboardPaths.static.meetings,
            search,
          }),
        pathname: employeeDashboardPaths.static.meetings,
      },
      {
        label: 'Vaults',
        icon: LockLightGrayIcon,
        activeIcon: LockWhiteIcon,
        disabledIcon: LockLightGrayIcon,
        pathname: employeeDashboardPaths.static.secretManager,
        searchKeywords: ['vault', 'secrets', 'keys', 'credentials'],
        tooltipHelpText:
          'Access vaults shared with you and copy secret values needed for your work.',
        onClick: () =>
          navigate({
            pathname: employeeDashboardPaths.static.secretManager,
            search,
          }),
      },
      // {
      //   label: 'Reports',
      //   icon: FileGreyIcon,
      //   activeIcon: FileWhiteIcon,
      //   disabledIcon: FileGreyIcon,
      //   pathname: employeeDashboardPaths.static.reports,
      //   onClick: () => navigate({ pathname: employeeDashboardPaths.static.reports, search }),
      // },
    ],
    [navigate, search],
  );
};
